<!-->┌──────────────────────────────────────────────────────────────────────────────────────┐<- ->
<- ->│                                Transaction History                                   │<- ->
<- ->└──────────────────────────────────────────────────────────────────────────────────────┘<--->

<template>
    <div class="rewards-transactions" v-if="loaded">
        <slot name="header"><h3 class="rewards-title">Transaction History</h3></slot>

        <div class="transactions-filters">

            <!-- FILTER -->
            <div class="labeled-select-input filter-options">
                <!--<label>Filter</label>-->
                <select v-model="filterSelection">
                    <option value="all">All Transactions</option>
                    <option v-for="range in validDateRanges" :key="range.id" :value="range">Last {{ range }} Months</option>
                    <option value="customRange">Date Range</option>
                </select>
            </div>

            <!-- DATE RANGE -->
            <transition-expand v-if="customRangeSelected" containerClass="date-range">
                <div class="date-inputs">
                    <input type="date" name="startDate" value="" class="start-date" v-model="startDate" />
                    <input type="date" name="endDate" value="" class="end-date" v-model="endDate" />
                </div>
            </transition-expand>
        </div>

        <!--Modified-->

        <Pagination :currentPage.sync="transactionsPageNum" :currentPageSize.sync="transactionsPageSize" :recordCount="transactionsRecordCount" />

        <div v-if="displayedTransactions.length > 0" :class="['list-grid', { hasTiers }]">
            <template v-if="!$screen.isMobile && !$screen.isTablet">
                <strong class="list-heading entry-date underline"
                        :class="getSortClass('entryDate')"
                        @click="sortCriteria('entryDate')">Date</strong>
                <strong class="list-heading pnr-number"
                        :class="getSortClass('pnr')"
                        @click="sortCriteria('pnr')">PNR</strong>
                <strong class="list-heading route"
                        :class="getSortClass('origin')"
                        @click="sortCriteria('origin')">Route</strong>
                <strong class="list-heading flight-number"
                        :class="getSortClass('flightNumber')"
                        @click="sortCriteria('flightNumber')">Flight</strong>
                <strong class="list-heading flight-date underline"
                        :class="getSortClass('firstDeparture')"
                        @click="sortCriteria('firstDeparture')">Flight Date</strong>
                <strong class="list-heading description"
                        :class="getSortClass('description')"
                        @click="sortCriteria('description')">Description</strong>
                <strong class="list-heading miles underline"
                        :class="getSortClass('rewards')"
                        @click="sortCriteria('rewards')">Miles</strong>
                <strong v-if="hasTiers"
                        class="list-heading tier-miles"
                        :class="getSortClass('tierMiles')"
                        @click="sortCriteria('tierMiles')">Tier Miles</strong>
            </template>
            <template v-for="(flight, n) in displayedTransactions">
                <!-- Entry Date -->
                <span :class="{'list-item entry-date':1, even:n%2}">
                    <date-value :value="flight.entryDate" :key="`[${n}].entryDate`">Expired</date-value>
                </span>

                <!-- PNR Number -->
                <span :class="{'list-item pnr-number':1, even:n%2}">
                    <span v-if="!flight.flight || !flight.flight.pnr">-</span>
                    <a v-else :href="lookupPNRLink(flight.flight.pnr, flight.flight.auth || memberDetails.member.lastName)">{{ flight.flight.pnr }}</a>
                </span>

                <!-- Route -->
                <span :class="{'list-item route':1, even:n%2}">
                    <span v-if="flight.flight && flight.flight.origin && flight.flight.destination">{{ flight.flight.origin }}&ndash;{{ flight.flight.destination }}</span>
                    <span v-else>-</span>
                </span>

                <!-- Flight Number -->
                <span :class="{'list-item flight-number':1, even:n%2}">
                    <strong v-if="$screen.isMobile || $screen.isTablet" class="title">Flight No.</strong>
                    <span v-if="flight.flight && flight.flight.flightNumber">{{ flight.flight.flightNumber }}</span>
                    <span v-else>-</span>
                </span>

                <!-- Flight Date -->
                <span :class="{'list-item flight-date':1, even:n%2}">
                    <strong v-if="$screen.isMobile || $screen.isTablet" class="title">Flight Date</strong>
                    <date-value :value="flight.flight && flight.flight.firstDeparture" :key="`[${n}].flightDate`">-</date-value>
                </span>

                <!-- Description -->
                <span :class="{'list-item description':1, even:n%2, negative:flight.rewards<=0}">
                    <span v-if="$screen.isMobile || $screen.isTablet" class="value">{{ flight.rewards }} Miles</span>
                    <span class="name" v-if="flight.description">{{ flight.description }}</span>
                    <span v-else>-</span>
                </span>

                <!-- Airline Miles -->
                <span v-if="!$screen.isMobile && !$screen.isTablet" :class="{'list-item miles':1, even:n%2, negative:flight.rewards<=0}">
                    <span v-if="flight.flight && flight.flight.rewards">{{ flight.rewards | formatNumber }}</span>
                    <span v-if="flight.rewards">{{ flight.rewards | formatNumber }}</span>
                </span>

                <!-- Tier Miles -->
                <span v-if="hasTiers && !$screen.isMobile && !$screen.isTablet" :class="{'list-item tier-miles':1, even:n%2, negative:flight.rewards<=0}">
                    <span v-if="flight.isTierMile">{{ flight.rewards | formatNumber }}</span>
                    <span v-else>-</span>
                </span>

            </template>
            <template v-if="!$screen.isMobile && !$screen.isTablet">
                <span class="page-total-title">Page Totals:</span>
                <span class="page-total-miles">{{ transactionsPageTotalMiles | formatNumber }}</span>
                <span v-if="hasTiers" class="page-total-tier-miles">{{ transactionsPageTotalTierMiles | formatNumber }}</span>

                <span class="total-title">Grand Totals:</span>
                <span class="total-miles">{{ transactionsTotalMiles | formatNumber }}</span>
                <span v-if="hasTiers" class="total-tier-miles">{{ transactionsTotalTierMiles | formatNumber }}</span>
            </template>
            <template v-else>
                <span class="list-item totals">
                    <span class="page-total-title">Page Totals:</span>
                    <span class="page-total-miles">{{ transactionsPageTotalMiles | formatNumber }} Miles</span>
                    <span v-if="hasTiers" class="page-total-tier-miles">{{ transactionsPageTotalTierMiles | formatNumber }} Tier Miles</span>
                </span>
                <span class="list-item totals">
                    <span class="total-title">Grand Totals:</span>
                    <span class="total-miles">{{ transactionsTotalMiles | formatNumber }} Miles</span>
                    <span v-if="hasTiers" class="total-tier-miles">{{ transactionsTotalTierMiles | formatNumber }} Tier Miles</span>
                </span>
            </template>

        </div>

        <transition-expand v-if="error || exception" classList="conflicts-container">
            <conflicts :error.sync="error" :exception.sync="exception" :is-sys-admin="isSysAdmin"></conflicts>
        </transition-expand>
    </div>
</template>

<script>
    import { fetchJSON } from "@/helpers/fetch"
    import conflicts, { CONFLICTS } from "@/common/conflicts"
    import Pagination from "@/common/pagination"
    import * as format from "@/helpers/format"

    export default {
        name: 'TransactionHistory',
        components: { conflicts, Pagination },
        mixins: [CONFLICTS],
        filters: { ...format },

        emits: ['loading', 'loaded', 'update:member-details'],

        props: {
            memberDetails: Object,
            months: [Number, String]
        },

        data() {
            let { member, account } = this.memberDetails || {}

            return {
                customRangeSelected: false,
                filterSelection: "all",
                tabSelection: 'flightsFlown',

                startDate: null,
                endDate: null,

                allTransactions: null,
                transactionsPageNum: 0,
                transactionsPageSize: 10,
                transactionsRecordCount: null,

                account,
                accountMilesBalance: account && account.milesBalance,
                accountTierMilesBalance: account && account.tierMilesBalance,

                requiredMonths: null,

                tierInfo: member && member.tierInfo,

                currentSort: 'entryDate',
                currentSortDir: 'desc',

                loaded: false
            }
        },

        mounted() {
            this.getRequiredMonths()

            let endpoints = [this.getTransactionHistory()]
            if (!this.memberDetails) endpoints.push(this.getMemberDetails())

            this.$emit('loading')

            Promise.all(endpoints)
                .then(() => {
                    this.loaded = true
                    this.$emit('loaded')
                })
                .catch(() => this.$emit('close'))
        },

        methods: {

            sortCriteria(filter) {
                if (filter == this.currentSort) {
                    this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc';
                }
                this.currentSort = filter;
            },

            getSortClass(columnName) {
                if (this.currentSort == columnName)
                    return (this.currentSortDir == 'desc') ? 'forwardSort' : 'reverseSort'
            },

            getMemberDetails() {
                return fetchJSON(`/api/members/GetMemberDetails?verbose=true`)
                    .then(data => {
                        this.tierInfo = data.member.tierInfo;
                        this.accountMilesBalance = data.account.milesBalance;
                        this.accountTierMilesBalance = data.account.tierMilesBalance;
                        this.account = data.account;

                        this.$emit('update:member-details', data)
                    })
            },

            getTransactionHistory() {
                return fetchJSON(`/api/members/GetTransactionHistory`)
                    .then(data => {
                        this.allTransactions = data.transactions;
                    })
            },

            getRequiredMonths() {
                if (this.months) {
                    this.filterSelection = this.months;
                }
            },

            lookupPNRLink(pnr, auth) {
                if (pnr && auth) return `/bookings/view_reservation.aspx?pnr=${pnr}&auth=${auth}`
            }
        },

        computed: {

            sortedTransactions() {
                if (this.filteredTransactions) {
                    return this.filteredTransactions.sort((a, b) => {
                        let modifier = 1;
                        if (this.currentSortDir == 'desc') modifier = -1;

                        //Sort by First Departure
                        if (this.currentSort == 'firstDeparture' && a.flight && b.flight) {
                            if (a.flight[this.currentSort] < b.flight[this.currentSort]) return -1 * modifier;
                            if (a.flight[this.currentSort] > b.flight[this.currentSort]) return 1 * modifier;
                        }

                        //Sort by PNR#
                        if (this.currentSort == 'pnr' && a.flight && b.flight) {
                            if (a.flight[this.currentSort] < b.flight[this.currentSort]) return -1 * modifier;
                            if (a.flight[this.currentSort] > b.flight[this.currentSort]) return 1 * modifier;
                        }

                        //Sort by Route
                        if (this.currentSort == 'origin' && a.flight && b.flight) {
                            if (a.flight[this.currentSort] < b.flight[this.currentSort]) return -1 * modifier;
                            if (a.flight[this.currentSort] > b.flight[this.currentSort]) return 1 * modifier;
                        }

                        //Sort by Flight Number
                        if (this.currentSort == 'flightNumber' && a.flight && b.flight) {
                            if (parseInt(a.flight['flightNumber']) < parseInt(b.flight['flightNumber'])) return -1 * modifier;
                            if (parseInt(a.flight['flightNumber']) > parseInt(b.flight['flightNumber'])) return 1 * modifier;
                        }

                        //Sort by Description
                        if (this.currentSort == 'description' && a.flight && b.flight) {
                            if (parseInt(a.flight['description']) < parseInt(b.flight['description'])) return -1 * modifier;
                            if (parseInt(a.flight['description']) > parseInt(b.flight['description'])) return 1 * modifier;
                        }

                        //Sort by Tier Miles
                        if (this.currentSort == 'tierMiles' && a.flight && b.flight) {
                            if (a.rewards < b.rewards) return -1 * modifier;
                            if (a.rewards > b.rewards) return 1 * modifier;
                        }

                        //Default Sort
                        if (a[this.currentSort] < b[this.currentSort]) return -1 * modifier;
                        if (a[this.currentSort] > b[this.currentSort]) return 1 * modifier;
                        return 0;
                    });
                }
                return this.filteredTransactions;
            },

            filteredTransactionsFlights() {
                return this.filteredTransactions.filter(transaction => transaction.flight != null);
            },

            filteredTransactionsRewards() {
                return this.filteredTransactions.filter(transaction => transaction.flight == null);
            },

            filteredTransactions() {
                switch (this.filterSelection) {
                    case 'all':
                        this.customRangeSelected = false;
                        return this.allTransactions;
                        break;

                    case 'requiredMonths':
                        this.customRangeSelected = false;
                        if (this.allTransactions) {
                            let requiredMonthsAgo = new Date(new Date().setMonth(new Date().getMonth() - this.requiredMonths));
                            return this.allTransactions.filter(transaction => Date.parse(transaction.entryDate) > requiredMonthsAgo);
                        }
                        break;

                    case 6:
                    case 12:
                    case 24:
                    case 36:
                    case 48:
                    case 60:
                    case 72:
                        this.customRangeSelected = false;
                        if (this.allTransactions) {
                            let requiredMonthsAgo = new Date(new Date().setMonth(new Date().getMonth() - this.filterSelection));
                            return this.allTransactions.filter(transaction => Date.parse(transaction.entryDate) > requiredMonthsAgo);
                        }
                        break;

                    case 'customRange':
                        this.customRangeSelected = true;

                        //Both start and endDate provided
                        if (this.startDate && this.endDate) {
                            return this.allTransactions.filter(transaction => transaction.entryDate >= new Date(Date.parse(this.startDate)) && transaction.entryDate <= new Date(Date.parse(this.endDate)));
                        }

                        //Only startDate provided. Filter from date to present
                        if (this.startDate && !this.endDate) {
                            return this.allTransactions.filter(transaction => transaction.entryDate >= new Date(Date.parse(this.startDate)));
                        }

                        //Only endDate provided. Filter from start of time to date
                        if (!this.startDate && this.endDate) {
                            return this.allTransactions.filter(transaction => transaction.entryDate <= new Date(Date.parse(this.endDate)));
                        }

                        //Neither startDate nor endDate provided
                        return [];
                }
            },

            displayedTransactions() {
                if (this.sortedTransactions) {
                    this.transactionsRecordCount = this.sortedTransactions.length;

                    const minTransactionIndex = (((this.transactionsPageNum + 1) * this.transactionsPageSize) - this.transactionsPageSize);
                    const maxTransactionIndex = ((this.transactionsPageNum + 1) * this.transactionsPageSize);
                    return this.sortedTransactions.slice(minTransactionIndex, maxTransactionIndex);
                }
                return [];
            },

            hasTiers() {
                try { return this.CONFIG.RewardsProgram.HasTiers } catch { }
            },

            tierMiles() {
                if (this.account && this.account.tierMilesBalance) return this.account.tierMilesBalance;
                return 0;
            },

            //Page Miles
            transactionsPageTotalMiles() {
                return this.displayedTransactions.reduce((prev, current) => prev + current.rewards, 0);
            },

            //Page Tier Miles
            transactionsPageTotalTierMiles() {
                let total = 0;
                this.displayedTransactions.forEach(transaction => {
                    if (transaction.isTierMile) {
                        total += transaction.rewards;
                    }
                })
                return total;
            },

            //Overall Miles
            transactionsTotalMiles() {
                if (this.filteredTransactions) {
                    let total = 0;
                    this.filteredTransactions.forEach(transaction => {
                        total += transaction.rewards;
                    })
                    return total;
                }
                return 0;
            },

            //Overall Tier Miles
            transactionsTotalTierMiles() {
                if (this.filteredTransactions) {
                    let total = 0;
                    this.filteredTransactions.forEach(transaction => {
                        if (transaction.isTierMile) total += transaction.rewards;
                    })
                    return total;
                }
                return 0;
            },

            validDateRanges() {
                if (this.tierInfo) {
                    //To keep 12 months as a default option, we add it in manually.
                    let validMonths = [12];

                    for (let i = 0; i < this.tierInfo.length; i++) {
                        //If value is not null and not a duplicate of 12 months
                        if (this.tierInfo[i].requiredMonths && this.tierInfo[i].requiredMonths != 12) validMonths.push(this.tierInfo[i].requiredMonths);
                    }

                    //Remove duplicates and sort results
                    return [...new Set(validMonths.sort((a, b) => a - b))];
                }
            }
        }
    }
</script>